import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { resolveMedia } from "../siteContext";
import { useLang, pick } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const { lang, s } = useLang();
  const [posts, setPosts] = useState(null);
  const Arrow = lang === "ar" ? ArrowLeft : ArrowRight;

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/posts`).then((res) => setPosts(Array.isArray(res.data) ? res.data : [])).catch(() => setPosts([]));
  }, []);

  return (
    <div className="min-h-screen bg-luxury-black">
      <Header />
      <main className="pt-36 pb-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl mb-16">
            <p className="text-luxury-gold text-sm tracking-[0.35em] mb-6">{s.blog_eyebrow}</p>
            <h1 data-testid="blog-page-title" className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-luxury-bone leading-[1.25]">
              {s.blog_title_a} <span className="gold-gradient-text italic">{s.blog_title_b}</span>
            </h1>
          </motion.div>

          {posts === null ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>
          ) : posts.length === 0 ? (
            <p data-testid="blog-empty" className="text-luxury-gray border border-luxury-border bg-luxury-surface p-12 text-center">{s.blog_empty}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((p, i) => (
                <motion.article
                  key={p.id}
                  data-testid={`blog-page-card-${i}`}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="group bg-luxury-surface border border-luxury-border rounded-2xl overflow-hidden hover:border-luxury-gold hover:-translate-y-2 transition-all duration-500 flex flex-col"
                >
                  {p.cover_image ? (
                    <div className="h-52 overflow-hidden">
                      <img src={resolveMedia(p.cover_image)} alt={pick(lang, p, "title")} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    </div>
                  ) : null}
                  <div className="p-7 flex flex-col flex-1">
                    <p className="text-luxury-gray text-xs flex items-center gap-2 mb-3">
                      <CalendarDays className="w-3.5 h-3.5 text-luxury-gold" strokeWidth={1.5} />
                      {(p.created_at || "").slice(0, 10)}
                    </p>
                    <h2 className="font-serif text-2xl text-luxury-bone mb-3 leading-snug">{pick(lang, p, "title")}</h2>
                    <p className="text-luxury-gray text-sm leading-relaxed flex-1 line-clamp-3">{pick(lang, p, "excerpt")}</p>
                    <Link to={`/blog/${p.slug}`} data-testid={`blog-page-read-${i}`} className="mt-6 inline-flex items-center gap-2 text-sm text-luxury-gold hover:text-luxury-goldLight transition-colors">
                      {s.blog_read}
                      <Arrow className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
